import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { formatNumber } from '@/lib/format'
import type { DailyNewCustomers } from '@/api/types'

const AXIS_COLOR = 'var(--color-muted-foreground)'
const GRID_COLOR = 'var(--color-border)'
const BAR_COLOR = 'var(--color-chart-1)'

// 'YYYY-MM-DD' tolkas som lokal tid (inte UTC) så etiketten inte hamnar en dag fel för vissa tidszoner.
function formatAxisDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString('sv-SE', { day: 'numeric', month: 'numeric' })
}

function formatTooltipDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'long' })
}

interface ChartTooltipProps {
  active?: boolean
  payload?: { payload: DailyNewCustomers }[]
}

function ChartTooltip({ active, payload }: ChartTooltipProps): React.ReactElement | null {
  const point = payload?.[0]?.payload

  if (!active || !point) {
    return null
  }

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm">
      <p className="font-medium">{formatTooltipDate(point.date)}</p>
      <p className="text-muted-foreground">{formatNumber(point.count)} nya kunder</p>
    </div>
  )
}

export function DailyNewCustomersChart({ data }: { data: DailyNewCustomers[] }): React.ReactElement {
  const hasNoData = data.length === 0

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle className="text-2xl font-serif text-foreground">Nya kunder per dag</CardTitle>
        <CardDescription>Senaste 30 dagarna.</CardDescription>
      </CardHeader>
      <CardContent>
        {hasNoData && <p className="text-sm text-muted-foreground">Ingen data att visa.</p>}

        {!hasNoData && (
          <div className="h-72 w-full">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={data}
                margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke={GRID_COLOR}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatAxisDate}
                  interval={4}
                  tickLine={false}
                  axisLine={{ stroke: GRID_COLOR }}
                  tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                />
                <YAxis
                  allowDecimals={false}
                  width={32}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: 'var(--color-muted)' }}
                  content={<ChartTooltip />}
                />
                <Bar
                  dataKey="count"
                  fill={BAR_COLOR}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
